import React, { useEffect, useState } from 'react';
import { X, MapPin, Calendar, Package, User, AlertCircle, Mail } from 'lucide-react';
import { Visit, Product, Order, ProductPlacementEntry, SalesPerformance } from '../../types';
import { api } from '../../services/api';
import { formatDate, formatDateTime } from '../../utils/datetime';
import {
  useVisitNotes,
  VisitNotesSection,
} from './VisitNoteTimeline';
import { VisitFollowupEmailModal } from './VisitFollowupEmailModal';
import { VisitPlacementsSection } from './VisitPlacementsSection';
import { VisitSalesPerformanceSection } from './VisitSalesPerformanceSection';
import { VisitOrdersSection } from './VisitOrdersSection';
import { ResolvedTask, VisitResolvedTasksSection } from './VisitResolvedTasksSection';

interface VisitDetailModalProps {
  visit: Visit | null;
  isOpen: boolean;
  onClose: () => void;
}

export const VisitDetailModal: React.FC<VisitDetailModalProps> = ({ visit, isOpen, onClose }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [resolvedTasks, setResolvedTasks] = useState<ResolvedTask[]>([]);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const { notes: visitNotes, timelineEmpty } = useVisitNotes(
    visit?.pos_id ?? '',
    visit?.id ?? '',
    isOpen && !!visit
  );

  useEffect(() => {
    if (!isOpen || !visit) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const productsRes = await api.get('/products');
        if (!cancelled) setProducts(productsRes.data);
        try {
          const ordersRes = await api.get(`/orders?visit_id=${visit.id}`);
          if (!cancelled) setOrders(ordersRes.data || []);
        } catch (err) {
          console.error('Failed to fetch orders:', err);
          if (!cancelled) setOrders([]);
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, visit]);

  useEffect(() => {
    if (!isOpen || !visit) {
      setResolvedTasks([]);
      return;
    }
    if (visit.is_followup_resolution === false) {
      setResolvedTasks([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/visits/${visit.id}/resolved-tasks`);
        if (!cancelled) setResolvedTasks(res.data || []);
      } catch (err) {
        console.error('Failed to fetch resolved tasks:', err);
        if (!cancelled) setResolvedTasks([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, visit]);

  if (!isOpen || !visit) return null;

  // Older rows still hold JSON strings in the jsonb columns, newer ones parsed objects.
  const parsePlacementData = <T,>(data: T | string | undefined | null): T | null => {
    if (!data) return null;
    if (typeof data === 'string') {
      try {
        return JSON.parse(data) as T;
      } catch (e) {
        console.error('Failed to parse placement data:', e);
        return null;
      }
    }
    return data;
  };

  const getProductName = (productId: string): string => {
    const product = products.find(p => p.id === productId);
    return product ? `${product.name} (${product.manufacturer_name})` : 'Loading...';
  };

  const productPlacements = parsePlacementData<Record<string, ProductPlacementEntry>>(
    visit.product_placements
  );
  const visitProducts = parsePlacementData<string[]>(visit.visit_products);
  const salesPerformance = parsePlacementData<SalesPerformance>(visit.sales_performance);
  const followupMaterials = parsePlacementData<string[]>(visit.followup_materials) || [];
  const followupActions = parsePlacementData<string[]>(visit.followup_actions) || [];

  const getOutcomeColor = (outcome: string) => {
    switch (outcome) {
      case 'placement_secured': return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
      case 'follow_up_needed': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'competitor_exclusive': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-black text-gray-900">Visit Details</h2>
            <p className="text-sm text-gray-500">{formatDateTime(visit.created_at)}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setEmailModalOpen(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-teal-700 text-white
                font-bold text-sm hover:bg-teal-800 transition-colors"
            >
              <Mail size={16} />
              <span className="hidden sm:inline">Nachfass-E-Mail</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <MapPin size={18} className="text-gray-600" />
                <h3 className="font-bold text-gray-900">Point of Sale</h3>
              </div>
              <p className="text-lg font-black text-brand-600">{visit.pos_name || 'N/A'}</p>
              <p className="text-sm text-gray-600">{visit.pos_address || ''}</p>
              <p className="text-xs text-gray-500 mt-1">{visit.pos_city || ''}</p>
            </div>

            <div className="bg-gray-50 p-4 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <User size={18} className="text-gray-600" />
                <h3 className="font-bold text-gray-900">Sales Agent</h3>
              </div>
              <p className="text-lg font-black text-gray-800">{visit.user_name || 'N/A'}</p>
              <p className="text-sm text-gray-600">Visit Type: <span className="font-bold capitalize">{visit.visit_type?.replace('_', ' ')}</span></p>
            </div>
          </div>

          {/* Visit Outcome */}
          <div className="bg-gray-50 p-4 rounded-xl">
            <h3 className="font-bold text-gray-900 mb-3">Visit Outcome</h3>
            <div className="flex items-center gap-3">
              <span className={`px-4 py-2 rounded-lg font-bold border-2 ${getOutcomeColor(visit.visit_outcome || '')}`}>
                {visit.visit_outcome?.replace('_', ' ').toUpperCase()}
              </span>
              {visit.rejection_reason && (
                <div className="flex-1 bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800"><strong>Reason:</strong> {visit.rejection_reason}</p>
                </div>
              )}
            </div>
          </div>

          {/* Products */}
          {(visit.product_name || (visitProducts?.length ?? 0) > 0 || visit.product_id) && (
            <div className="bg-gray-50 p-4 rounded-xl">
              <div className="flex items-center gap-2 mb-3">
                <Package size={18} className="text-gray-600" />
                <h3 className="font-bold text-gray-900">
                  {visit.visit_type === 'revisit' ? 'Products' : 'Products Introduced'}
                </h3>
              </div>
              {!loading ? (
                <div className="space-y-2">
                  {visitProducts && Array.isArray(visitProducts) && visitProducts.length > 0 ? (
                    visitProducts.map((productId: string, index: number) => (
                      <div key={productId || index} className="px-4 py-2 bg-white border border-blue-200 rounded-lg">
                        <p className="text-sm font-bold text-blue-900">{getProductName(productId)}</p>
                      </div>
                    ))
                  ) : visit.product_id ? (
                    <div className="px-4 py-2 bg-white border border-blue-200 rounded-lg">
                      <p className="text-sm font-bold text-blue-900">{getProductName(visit.product_id)}</p>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-700">{visit.product_name || 'N/A'}</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">Loading products...</p>
              )}
            </div>
          )}

          {productPlacements && !loading && (
            <VisitPlacementsSection
              placements={productPlacements}
              resolveProductName={getProductName}
            />
          )}

          {salesPerformance && !loading && (
            <VisitSalesPerformanceSection
              performance={salesPerformance}
              resolveProductName={getProductName}
            />
          )}

          {!loading && <VisitOrdersSection orders={orders} resolveProductName={getProductName} />}

          <VisitResolvedTasksSection tasks={resolvedTasks} />

          {/* Follow-up Actions */}
          {visit.needs_followup && (
            <div className={`p-4 rounded-xl border-2 ${
              visit.followup_priority === 'urgent' ? 'bg-fuchsia-50 border-fuchsia-300' :
              visit.followup_priority === 'high' ? 'bg-red-50 border-red-300' :
              visit.followup_priority === 'medium' ? 'bg-orange-50 border-orange-300' :
              'bg-blue-50 border-blue-300'
            }`}>
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle size={18} className={
                  visit.followup_priority === 'urgent' ? 'text-fuchsia-700' :
                  visit.followup_priority === 'high' ? 'text-red-600' :
                  visit.followup_priority === 'medium' ? 'text-orange-600' :
                  'text-blue-600'
                } />
                <h3 className="font-bold text-gray-900">Follow-up Required</h3>
                <span className={`px-2 py-1 rounded-lg text-xs font-bold ${
                  visit.followup_priority === 'urgent' ? 'bg-fuchsia-700 text-white' :
                  visit.followup_priority === 'high' ? 'bg-red-600 text-white' :
                  visit.followup_priority === 'medium' ? 'bg-orange-600 text-white' :
                  'bg-blue-600 text-white'
                }`}>
                  {visit.followup_priority?.toUpperCase()}
                </span>
              </div>

              {followupMaterials.length > 0 && (
                <div className="mb-3">
                  <p className="text-sm font-bold text-gray-700 mb-1">Materials to Provide:</p>
                  <div className="flex flex-wrap gap-2">
                    {followupMaterials.map((material: string) => (
                      <span key={material} className="px-2 py-1 bg-white rounded-md text-xs font-medium text-gray-700 border border-gray-300">
                        {material.replace('_', ' ').toUpperCase()}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {followupActions.length > 0 && (
                <div className="mb-3">
                  <p className="text-sm font-bold text-gray-700 mb-1">Actions Required:</p>
                  <div className="flex flex-wrap gap-2">
                    {followupActions.map((action: string) => (
                      <span key={action} className="px-2 py-1 bg-white rounded-md text-xs font-medium text-gray-700 border border-gray-300">
                        {action.replace('_', ' ').toUpperCase()}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {timelineEmpty && visit.followup_notes && (
                <div className="bg-white p-3 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-700">{visit.followup_notes}</p>
                </div>
              )}

              {visit.followup_due_date && (
                <p className="text-sm text-gray-600 mt-2">
                  <Calendar size={14} className="inline mr-1" />
                  Due: {formatDate(visit.followup_due_date)}
                </p>
              )}
            </div>
          )}

          <VisitNotesSection
            notes={visitNotes}
            timelineEmpty={timelineEmpty}
            legacyNotes={visit.notes}
          />

          {/* Metadata */}
          <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
            <div>
              <span className="font-bold text-gray-700">Created:</span> {formatDateTime(visit.created_at)}
            </div>
            <div>
              <span className="font-bold text-gray-700">Updated:</span> {formatDateTime(visit.updated_at)}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
          <button
            onClick={onClose}
            className="w-full py-3 bg-gray-800 text-white rounded-xl font-bold hover:bg-gray-900 transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      {/* Wrapped so clicks inside the mail dialog do not reach the backdrop of this modal. */}
      <div onClick={(e) => e.stopPropagation()}>
        <VisitFollowupEmailModal
          visitId={visit.id}
          isOpen={emailModalOpen}
          onClose={() => setEmailModalOpen(false)}
        />
      </div>
    </div>
  );
};
